/* MFF — overlay control fix (Claude, Aug 2026)

   This theme renders its overlay controls correctly but never opens any of
   them, because nothing is bound to the click:

     1. the language selector      (announcement bar + mobile drawer)
     2. the country / currency selector
     3. the cart drawer            (customers were bounced to /cart instead)
     4. the cart upsell block (offers the course matching the cart contents)

   Symptoms: a real click leaves aria-expanded="false"; dropdown panels keep
   .opacity-0 .invisible .pointer-events-none; the cart drawer keeps its
   `inert` attribute and computes to display:none. The country panel also
   carries an inline style="opacity:0;visibility:hidden" written by another
   script, so removing classes alone is not enough.

   It also opens the drawer after an add-to-cart, which the theme never did,
   and trims two oversized headings inside the drawer.

   Oct 2026 additions:
     5. closing a drawer now runs the theme's own hide(), so the page is no
        longer left scroll-locked (<body class="has-modal-open">) after the
        menu or cart drawer is closed; plus a guard that releases a lock that
        has no open panel behind it
     6. the Add to Cart button shows it is working and ignores a second tap
        until the first add has finished
     7. the email-offer teaser no longer sits on top of the mobile dock or the
        cart drawer buttons, and the cookie bar no longer blocks the page
     8. (23 Oct) language / country selectors:
        - desktop dropdowns are toggled in ONE place (a capture listener with
          its own open flag). Before, the theme's toggle and this file's
          drawer code both reacted to the same click, so the list opened late,
          or opened and closed again.
        - an open list is kept visible by a stylesheet rule on that flag, because
          another script rewrites the country list's inline opacity/visibility
          straight after the click.
        - the language / country lists inside the mobile menu are left to the
          theme. Opening them here ran closeAllDrawers(), which closed the
          menu around them, so nothing appeared on phones.

   theme.js is not modified. */
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return; /* leave the editor alone */

  var HIDE = ['opacity-0', 'invisible', 'pointer-events-none'];
  var ITEM = '.localization__item[role="combobox"]';
  var DRAWER_ID = 'CartDrawer';

  /* ------------------------------------------------------------------ *
   * type scale — the empty-cart heading shipped at 48px                  *
   * ------------------------------------------------------------------ */
  var css = document.createElement('style');
  css.textContent =
    '.cart-drawer .drawer__empty-text{font-size:1.5rem!important;line-height:1.15!important;max-width:18ch;margin-left:auto;margin-right:auto;}' +
    '.cart-drawer .drawer__title{font-size:1.25rem!important;}' +
    '@media (max-width:640px){.cart-drawer .drawer__empty-text{font-size:1.3rem!important;}.cart-drawer .drawer__title{font-size:1.1rem!important;}}' +
    /* email-offer teaser: keep it clear of the mobile dock, and out of the way while a drawer is open */
    '@media (max-width:767px){div[class*="kl-teaser-"]{bottom:calc(var(--mobile-dock-height,61px) + 10px)!important;}}' +
    '.has-modal-open div[class*="kl-teaser-"],.has-modal-opening div[class*="kl-teaser-"]{display:none!important;}' +
    'body:has(#CookieBanner[open]) div[class*="kl-teaser-"]{display:none!important;}' +
    /* cookie bar: stay visible at the bottom without covering or dimming the page */
    '#CookieBanner[open]{pointer-events:none!important;}#CookieBanner[open] .cookie-banner{pointer-events:auto!important;}#CookieBanner>overlay-element{display:none!important;}' +
    /* add-to-cart in progress */
    '.mff-adding{position:relative;pointer-events:none!important;opacity:.7;}' +
    '.mff-adding:after{content:"";position:absolute;top:50%;right:18px;width:16px;height:16px;margin-top:-8px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:mff-spin .6s linear infinite;}' +
    '@keyframes mff-spin{to{transform:rotate(360deg);}}' +
    /* an open language / country list stays visible whatever inline style another script writes */
    '.localization__item[data-mff-dd="1"] dropdown-localization,.localization__item[data-mff-dd="1"] .dropdown{opacity:1!important;visibility:visible!important;pointer-events:auto!important;}';
  (document.head || document.documentElement).appendChild(css);

  /* ------------------------------------------------------------------ *
   * 1 & 2 — localization dropdowns                                      *
   * ------------------------------------------------------------------ */
  function panelOf(item) {
    return item.querySelector('dropdown-localization') || item.querySelector('.dropdown');
  }

  function setDropdown(item, open) {
    var panel = panelOf(item);
    if (!panel) return;
    if (open) {
      HIDE.forEach(function (c) { panel.classList.remove(c); });
      panel.style.setProperty('opacity', '1', 'important');
      panel.style.setProperty('visibility', 'visible', 'important');
      panel.style.setProperty('pointer-events', 'auto', 'important');
      Array.prototype.forEach.call(panel.querySelectorAll('.opacity-0'), function (el) {
        el.classList.remove('opacity-0');
      });
    } else {
      HIDE.forEach(function (c) { panel.classList.add(c); });
      panel.style.removeProperty('opacity');
      panel.style.removeProperty('visibility');
      panel.style.removeProperty('pointer-events');
    }
    item.setAttribute('aria-expanded', open ? 'true' : 'false');
    item.setAttribute('data-mff-dd', open ? '1' : '');
  }

  function closeAllDropdowns(except) {
    Array.prototype.forEach.call(document.querySelectorAll(ITEM), function (i) {
      if (i !== except) setDropdown(i, false);
    });
  }

  /* ------------------------------------------------------------------ *
   * 3 — drawers                                                          *
   * ------------------------------------------------------------------ */
  function openDrawer(el, trigger) {
    if (!el) return;
    closeAllDrawers(el);
    el.removeAttribute('inert');
    el.style.removeProperty('display');
    el.classList.remove('pointer-events-none');
    el.setAttribute('data-mff-open', '');
    try { if (typeof el.show === 'function') el.show(); } catch (e) {}
    var c = el.querySelector('.drawer__close');
    if (c) c.classList.remove('hidden');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
  }

  /* The theme tracks open panels itself: while one is open <body> carries
     has-modal-open, which stops scrolling (and, on touch screens, every
     gesture). Only the panel's own hide() clears it, so a panel that is merely
     hidden leaves the whole page frozen. */
  function anyPanelOpen() {
    var list = document.querySelectorAll('[open]'), i, el;
    for (i = 0; i < list.length; i++) {
      el = list[i];
      if (typeof el.hide !== 'function' || typeof el.show !== 'function') continue; /* not a theme panel */
      if (el.shouldLock === false) continue;      /* e.g. the cookie bar never locks the page */
      if (el.hasAttribute('inert')) continue;
      if (el.getClientRects().length) return true; /* genuinely on screen */
    }
    return false;
  }

  function releaseLock() {
    if (anyPanelOpen()) return;
    document.body.classList.remove('has-modal-open', 'has-modal-opening');
    document.documentElement.style.overflow = '';
  }

  function closeDrawer(el) {
    if (!el) return;
    el.removeAttribute('data-mff-open');
    if (el.id) {
      Array.prototype.forEach.call(
        document.querySelectorAll('[aria-controls="' + el.id + '"]'),
        function (t) { t.setAttribute('aria-expanded', 'false'); }
      );
    }

    var settled = false;
    function settle() {
      if (settled) return;
      settled = true;
      if (el.hasAttribute('data-mff-open')) return; /* re-opened in the meantime */
      el.removeAttribute('open');
      el.removeAttribute('active');
      el.setAttribute('inert', '');
      el.classList.add('pointer-events-none');
      releaseLock();
    }

    var closing = null;
    try {
      if (typeof el.hide === 'function' && el.hasAttribute('open')) closing = el.hide();
    } catch (e) {}

    if (closing && typeof closing.then === 'function') {
      closing.then(settle, settle);
      setTimeout(settle, 1200);                   /* never wait on an animation that does not finish */
    } else {
      el.style.setProperty('display', 'none', 'important');
      settle();
    }
  }

  /* Safety net: if the page is locked but nothing is open, unlock it the moment
     the shopper tries to scroll, tap or type. */
  function unstick() {
    var c = document.body && document.body.classList;
    if (c && (c.contains('has-modal-open') || c.contains('has-modal-opening'))) releaseLock();
  }
  ['touchstart', 'wheel', 'keydown'].forEach(function (type) {
    window.addEventListener(type, unstick, { passive: true, capture: true });
  });

  function closeAllDrawers(except) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-mff-open]'), function (d) {
      if (d !== except) closeDrawer(d);
    });
  }

  /* ------------------------------------------------------------------ *
   * add to cart → refresh the drawer contents and slide it open          *
   * ------------------------------------------------------------------ */
  function onConsultationPage() {
    /* that page books a slot and goes straight to checkout on purpose */
    return /\/pages\/consultation/.test(window.location.pathname) ||
           !!document.getElementById('mff-booking-form');
  }

  function updateCartCount(n) {
    Array.prototype.forEach.call(document.querySelectorAll('cart-count, .cart-drawer-button .count'), function (el) {
      el.textContent = n;
      el.setAttribute('aria-label', n + (n === 1 ? ' item' : ' items'));
      el.style.display = n > 0 ? '' : 'none';
    });
  }

  /* show that an add is in flight, and swallow a second tap until it lands */
  var addingTimer = null;
  function setAdding(on) {
    Array.prototype.forEach.call(
      document.querySelectorAll('.mp-add-cart, form[action*="/cart/add"] [type="submit"]'),
      function (b) {
        if (on) { b.classList.add('mff-adding'); b.setAttribute('aria-busy', 'true'); }
        else { b.classList.remove('mff-adding'); b.removeAttribute('aria-busy'); }
      }
    );
    if (addingTimer) { clearTimeout(addingTimer); addingTimer = null; }
    if (on) addingTimer = setTimeout(function () { setAdding(false); }, 8000);
  }

  var refreshing = false;
  function refreshAndOpenDrawer() {
    if (refreshing) return;
    refreshing = true;
    var dr = document.getElementById(DRAWER_ID);
    if (!dr) { refreshing = false; setAdding(false); return; }
    var sid = dr.getAttribute('data-section-id');

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCartCount(cart.item_count); })
      .catch(function () {});

    var done = function () {
      refreshing = false;
      setAdding(false);
      openDrawer(document.getElementById(DRAWER_ID), document.querySelector('.cart-drawer-button'));
    };

    if (!sid) { done(); return; }
    fetch('/?section_id=' + encodeURIComponent(sid))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById(DRAWER_ID);
        if (fresh) dr.innerHTML = fresh.innerHTML;
      })
      .catch(function () {})
      .then(done, done);
  }

  /* a) catch the theme's own ajax adds */
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var isAdd = method === 'POST' && /\/cart\/add/.test(url);
      var out = nativeFetch.apply(this, arguments);
      if (isAdd && !onConsultationPage()) {
        setAdding(true);
        out.then(function (res) {
          if (res && res.ok) setTimeout(refreshAndOpenDrawer, 60);
          else setAdding(false);
        }).catch(function () { setAdding(false); });
      }
      return out;
    };
  }

  /* b) catch plain form posts that would otherwise reload onto /cart */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.id === 'mff-booking-form') return;
    if (e.defaultPrevented) return;               /* the theme is handling it */
    var action = form.getAttribute('action') || '';
    if (!/\/cart\/add/.test(action)) return;
    if (onConsultationPage()) return;

    e.preventDefault();
    var btn = form.querySelector('[type="submit"]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    fetch(action.replace(/(\/cart\/add)(\.js)?$/, '$1.js'), {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: new FormData(form)
    }).then(function (r) {
      if (btn) btn.removeAttribute('aria-busy');
      if (r.ok) refreshAndOpenDrawer();
      else form.submit();
    }).catch(function () {
      if (btn) btn.removeAttribute('aria-busy');
      form.submit();
    });
  }, false);

  /* ------------------------------------------------------------------ *
   * 8 — desktop language / country dropdowns: one toggle, in one place  *
   * Runs in the capture phase so the theme's own toggle (which flips     *
   * aria-expanded but never shows the list) and the drawer code below    *
   * never see the click. Links inside the list are left alone so they    *
   * still submit the language / country change.                         *
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    var item = t.closest(ITEM);
    if (!item) return;
    if (t.closest('a[data-value]')) return;                    /* a language / country choice */
    if (t.closest('dropdown-localization, .dropdown')) return; /* somewhere inside the open list */
    e.preventDefault();
    e.stopPropagation();
    var open = item.getAttribute('data-mff-dd') === '1';
    closeAllDropdowns(item);
    setDropdown(item, !open);
  }, true);

  /* ------------------------------------------------------------------ *
   * delegated click handling                                            *
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;

    var closeBtn = target.closest('.drawer__close, [data-drawer-close]');
    if (closeBtn) {
      var owner = closeBtn.closest('[data-mff-open]') || closeBtn.closest('[role="dialog"]');
      if (owner) { e.preventDefault(); closeDrawer(owner); return; }
    }

    /* the dialog fills the viewport and the panel is a child, so a click that
       lands on the dialog itself is a click on the backdrop */
    if (target.hasAttribute && target.hasAttribute('data-mff-open')) {
      closeDrawer(target);
      return;
    }

    /* desktop dropdowns are handled by the capture listener above */
    if (target.closest(ITEM)) return;

    /* the language / country lists inside the mobile menu belong to the theme:
       opening them as a drawer here closed the menu they live in */
    if (target.closest('[aria-controls^="Localization"], [id^="Localization"]')) return;

    var trigger = target.closest('[aria-controls]');
    if (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (panel && typeof panel.show === 'function') {
        e.preventDefault();
        if (panel.hasAttribute('data-mff-open')) closeDrawer(panel);
        else { closeAllDropdowns(null); openDrawer(panel, trigger); }
        return;
      }
    }

    closeAllDropdowns(null);
  }, false);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      closeAllDropdowns(null);
      closeAllDrawers(null);
    }
  });

  /* ------------------------------------------------------------------ *
   * 4 — cart upsell: offer the course that matches what's in the cart   *
   *                                                                     *
   * Digital only, so it works in every market and adds nothing to the   *
   * parcel. Suppressed when the customer already has that course.       *
   *                                                                     *
   * DISCOUNT_PCT is 0 until the Buy-X-Get-Y automatic discount exists.  *
   * Set it to 75 the moment that discount goes live and the block will  *
   * show the struck-through price and the saving.                       *
   * ------------------------------------------------------------------ */
  var DISCOUNT_PCT = 0;

  /* Ratings are shown only at MIN_REVIEWS or more. A 5.0 from one review
     reads as fabricated and a 3.5 from two actively repels. */
  var MIN_REVIEWS = 10;

  var COURSES = {
    fixYourFeet: { handle: '3255072', id: 10498397733210, title: 'Fix Your Feet',
                   rating: 4.8, reviews: 179, meta: 'The complete programme · lifetime access' },
    bunion:      { handle: '3056916', id: 10115039265114, title: 'Bunion &amp; Tailor’s Bunion Course',
                   rating: 4.9, reviews: 10, meta: 'Targeted programme · lifetime access' },
    flatFoot:    { handle: '3056907', id: 10115007283546, title: 'Flat-Foot Course',
                   rating: null, reviews: 1, meta: 'Targeted programme · lifetime access' },
    plantar:     { handle: '3056909', id: 10115015475546, title: 'Plantar Fascia Course',
                   rating: null, reviews: 0, meta: 'Targeted programme · lifetime access' },
    rigid:       { handle: '3056912', id: 10115023733082, title: 'Rigid-Foot Course',
                   rating: null, reviews: 0, meta: 'Targeted programme · lifetime access' }
  };

  /* first rule whose pattern appears in a cart line wins */
  var RULES = [
    { re: /bunion|tailor/i,        key: 'bunion' },
    { re: /flat[\s-]?foot/i,       key: 'flatFoot' },
    { re: /plantar/i,              key: 'plantar' },
    { re: /rigid|stiff/i,          key: 'rigid' }
  ];

  var ALL_COURSE_IDS = Object.keys(COURSES).map(function (k) { return COURSES[k].id; });

  function money(cents, fmt) {
    var v = (cents / 100).toFixed(2);
    if (fmt && fmt.indexOf('kr') !== -1) return v.replace('.', ',') + ' kr';
    if (fmt && fmt.indexOf('$') !== -1) return '$' + v;
    if (fmt && fmt.indexOf('£') !== -1) return '£' + v;
    return '€' + v;
  }

  function pickCourse(cart) {
    var haystack = cart.items.map(function (i) {
      return [i.product_title, i.title, i.handle, (i.product_type || '')].join(' ');
    }).join(' | ');
    var inCart = cart.items.map(function (i) { return i.product_id; });

    var chosen = null;
    for (var r = 0; r < RULES.length; r++) {
      if (RULES[r].re.test(haystack)) { chosen = COURSES[RULES[r].key]; break; }
    }
    if (!chosen) chosen = COURSES.fixYourFeet;

    /* already own the match? fall back to the flagship, then give up */
    if (inCart.indexOf(chosen.id) !== -1) chosen = COURSES.fixYourFeet;
    if (inCart.indexOf(chosen.id) !== -1) return null;

    /* nothing to upsell if the cart is only courses */
    var hasPhysical = cart.items.some(function (i) {
      return i.requires_shipping && ALL_COURSE_IDS.indexOf(i.product_id) === -1;
    });
    if (!hasPhysical) return null;

    return chosen;
  }

  function render(course, product, cart) {
    var v = product.variants[0];
    var full = v.price;
    var pay = DISCOUNT_PCT > 0 ? Math.round(full * (100 - DISCOUNT_PCT) / 100) : full;
    var fmt = cart.currency === 'SEK' ? 'kr' : (cart.currency === 'USD' ? '$' : (cart.currency === 'GBP' ? '£' : '€'));

    var proof = (course.rating && course.reviews >= MIN_REVIEWS)
      ? '<span class="mff-up__stars">★ ' + course.rating.toFixed(1) + '</span>' +
        '<span class="mff-up__count">' + course.reviews + ' reviews</span>'
      : '';

    var price = DISCOUNT_PCT > 0
      ? '<s class="mff-up__was">' + money(full, fmt) + '</s> <strong class="mff-up__now">' + money(pay, fmt) + '</strong>' +
        '<span class="mff-up__save">Save ' + DISCOUNT_PCT + '%</span>'
      : '<strong class="mff-up__now">' + money(full, fmt) + '</strong>';

    var img = (product.featured_image || (product.images && product.images[0]) || '');
    if (img && img.indexOf('//') === 0) img = 'https:' + img;

    return '' +
      '<div class="mff-up" data-mff-upsell>' +
        '<p class="mff-up__eyebrow">Recommended with your order</p>' +
        '<div class="mff-up__row">' +
          (img ? '<img class="mff-up__img" src="' + img + '&width=160" alt="" loading="lazy">' : '') +
          '<div class="mff-up__body">' +
            '<p class="mff-up__title">' + course.title + '</p>' +
            (proof ? '<p class="mff-up__proof">' + proof + '</p>' : '') +
            '<p class="mff-up__meta">' + course.meta + '</p>' +
            '<p class="mff-up__price">' + price + '</p>' +
          '</div>' +
          '<button type="button" class="mff-up__btn" data-variant="' + v.id + '">Add</button>' +
        '</div>' +
      '</div>';
  }

  var CSS = '' +
    '.mff-up{border:1px solid var(--mff-line,#E4DED5);border-radius:12px;padding:14px;margin:14px 0;background:#fff;}' +
    '.mff-up__eyebrow{font-size:.68rem;letter-spacing:.12em;text-transform:uppercase;color:#C4714A;font-weight:700;margin-bottom:10px;}' +
    '.mff-up__row{display:flex;gap:12px;align-items:center;}' +
    '.mff-up__img{width:56px;height:56px;object-fit:cover;border-radius:8px;flex-shrink:0;}' +
    '.mff-up__body{flex:1;min-width:0;}' +
    '.mff-up__title{font-weight:700;font-size:.9rem;line-height:1.25;color:#1A1614;}' +
    '.mff-up__proof{display:flex;gap:6px;align-items:center;margin-top:2px;}' +
    '.mff-up__stars{color:#C4714A;font-weight:700;font-size:.78rem;}' +
    '.mff-up__count{color:#6A5A50;font-size:.74rem;}' +
    '.mff-up__meta{color:#6A5A50;font-size:.74rem;margin-top:2px;}' +
    '.mff-up__price{margin-top:4px;font-size:.85rem;}' +
    '.mff-up__was{color:#9A8C82;margin-right:5px;}' +
    '.mff-up__now{color:#1A1614;}' +
    '.mff-up__save{background:#EAF2ED;color:#1A6B4A;font-size:.7rem;font-weight:700;padding:2px 7px;border-radius:999px;margin-left:6px;}' +
    '.mff-up__btn{flex-shrink:0;background:#C4714A;color:#fff;border:none;border-radius:8px;padding:9px 16px;font-weight:600;font-size:.82rem;cursor:pointer;}' +
    '.mff-up__btn:hover{background:#A55A37;}' +
    '.mff-up__btn[disabled]{opacity:.6;cursor:default;}';

  function injectCss() {
    if (document.getElementById('mff-up-css')) return;
    var s = document.createElement('style');
    s.id = 'mff-up-css'; s.textContent = CSS;
    document.head.appendChild(s);
  }

  function mountPoint(drawer) {
    /* Append inside the scrolling item list. Mounting it as a sibling of the
       footer made it a flex child of .drawer__inner, which stole height from
       .drawer__scrollable and collapsed the item list to a sliver. */
    return drawer.querySelector('.drawer__scrollable')
        || drawer.querySelector('.drawer__content')
        || drawer.querySelector('.drawer__inner') || drawer;
  }

  var building = false;
  async function buildUpsell() {
    if (building) return;
    building = true;
    try {
      var drawer = document.getElementById('CartDrawer');
      if (!drawer) return;
      var existing = drawer.querySelector('[data-mff-upsell]');
      if (existing) existing.remove();

      var cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
      if (!cart.items || !cart.items.length) return;

      var course = pickCourse(cart);
      if (!course) return;

      var product = await fetch('/products/' + course.handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; });
      if (!product || !product.variants || !product.variants.length) return;

      injectCss();
      var host = mountPoint(drawer);
      var wrap = document.createElement('div');
      wrap.innerHTML = render(course, product, cart);
      var node = wrap.firstChild;
      host.appendChild(node);

      node.querySelector('.mff-up__btn').addEventListener('click', function (e) {
        var btn = e.currentTarget;
        btn.disabled = true; btn.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(btn.getAttribute('data-variant'), 10), quantity: 1 }] })
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return refreshDrawer();
        }).catch(function () {
          btn.disabled = false; btn.textContent = 'Add';
        });
      });
    } catch (err) {
      /* an upsell must never break the cart */
    } finally {
      building = false;
    }
  }

  function refreshDrawer() {
    var drawer = document.getElementById('CartDrawer');
    var sid = drawer && drawer.getAttribute('data-section-id');
    if (!sid) { window.location.reload(); return; }
    return fetch('/?section_id=' + encodeURIComponent(sid))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('CartDrawer');
        if (fresh && drawer.parentNode) {
          var wasOpen = drawer.hasAttribute('data-mff-open');
          drawer.innerHTML = fresh.innerHTML;
          if (wasOpen) { drawer.setAttribute('data-mff-open', ''); drawer.removeAttribute('inert'); }
        }
        return buildUpsell();
      });
  }

  /* build whenever the drawer is opened */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    var trig = t.closest('[aria-controls="CartDrawer"], .cart-drawer-button');
    if (trig) setTimeout(buildUpsell, 350);
  }, false);

  /* and after anything else adds to the cart */
  (function (of) {
    window.fetch = function () {
      var url = arguments[0] && arguments[0].url ? arguments[0].url : arguments[0];
      var p = of.apply(this, arguments);
      if (typeof url === 'string' && /\/cart\/(add|change|update)/.test(url)) {
        p.then(function () { setTimeout(buildUpsell, 400); }).catch(function () {});
      }
      return p;
    };
  })(window.fetch);
})();
